import type { ReactNode } from 'react';

import { useAuth } from '../hooks/use-auth';
import { useOwnerMe } from '../hooks/use-owner-me';

import { FullPageLoader } from './full-page-loader';
import { Lockout } from './lockout';
import { Login } from './login';

/**
 * Three-state gate:
 *   1. Not signed in → <Login>
 *   2. Signed in but not in OWNER_EMAILS (admin-api returns 403) → <Lockout>
 *   3. Signed in + owner → render children
 *
 * The client-side check is purely UX — admin-api's `OwnerGuard` is the
 * real enforcer. A determined attacker who skips this UI still hits a
 * 403 server-side and gets zero data.
 */
export function OwnerGate({ children }: { readonly children: ReactNode }) {
  const { user, isInitialized } = useAuth();
  const me = useOwnerMe(!!user && isInitialized);

  if (!isInitialized) return <FullPageLoader message="Reading session…" />;
  if (!user) return <Login />;

  if (me.isLoading) return <FullPageLoader message="Verifying owner access…" />;

  const status = (me.error as { statusCode?: number } | undefined)?.statusCode;
  if (status === 403) return <Lockout />;
  // 401 or network — let the global retry/refresh handle and show
  // loader until we know. If repeatedly 401, the user will see Login
  // after Supabase clears the session.
  if (me.error || !me.data) return <FullPageLoader message="Connecting…" />;

  return <>{children}</>;
}
